import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import type { UseModalProps } from '@trinity/chat-components/components/Modal';
import { Modal, Header } from '@trinity/chat-components/components/Modal';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet';
import Button from '@trinity/chat-components/components/presentation/Button';
import { RenameFolderContext } from '@trinity/chat-components/containers/Workspace.Folder/context';

type Props = {
  folderId: number;
  workspaceId: number;
  name: string;
  onRenameSuccess?: (folderName: string) => void;
} & UseModalProps;

export const RenameFolderModal = ({
  folderId,
  workspaceId,
  name: initialName,
  onClose,
  open,
  onRenameSuccess,
}: Props) => {

  const [name, setName] = useState(initialName);
  const mutation = useContext(RenameFolderContext);

  const handleSave = useCallback(async () => {
    await mutation.rename({
      folderId,
      workspaceId,
      name,
    });
    onRenameSuccess?.(name);
    onClose();
  }, [folderId, mutation, name, onClose, workspaceId, onRenameSuccess]);

  const Footer = useCallback(() => {
    return (
      <ButtonSet>
        <StyledButton
          disabled={mutation.isLoading}
          color="destructive"
          onClick={onClose}
          variant="brick">
          Cancel
        </StyledButton>
        <StyledButton
          disabled={mutation.isLoading}
          color="primary"
          onClick={handleSave}
          variant="brick">
          Save
        </StyledButton>
      </ButtonSet>
    );
  }, [handleSave, mutation.isLoading, onClose]);

  return (
    <Modal open={open} onClose={onClose}>
      <Header>Rename Folder</Header>
      <TextField
        sx={{ marginBottom: 2 }}
        autoFocus
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        variant='standard'
        placeholder="Enter new folder name"
      />
      <Footer />
    </Modal>
  );
};

const StyledButton = styled(Button)({
  minWidth: 130,
});